import type { UserCommunication } from '@this+that/models';

import { DraftSyncStatus } from '../../../enums/draftSyncStatus';
import {
  type ComposeSnapshotIndexEntry,
  type SnapshotVisibility,
  selectUnsavedSnapshotEntry,
  useComposeSnapshotIndexStore,
} from '../../../store/ComposeSnapshotIndex';
import { getDraftSyncStatus } from '../../../utils/draftSyncStatus/getDraftSyncStatus';
import {
  fromLocalDraftSelectionId,
  isLocalDraftSelectionId,
} from '../../../utils/localDraftSelection';
import {
  resolveDraftId,
  threadCarriesDraft,
} from '../../../utils/userCommunicationHelpers';

export interface CommunicationDeletionPlan {
  serverDraftMessageIds: string[];
  localSessionIds: string[];
  otherIds: string[];
}

export const resolveLocalOnlySessionId = (
  userCommunication: UserCommunication,
  entriesBySessionId: Record<string, ComposeSnapshotIndexEntry>,
  visibility?: SnapshotVisibility,
): string | undefined => {
  const entry = selectUnsavedSnapshotEntry(
    entriesBySessionId,
    userCommunication,
    visibility,
  );
  return getDraftSyncStatus(entry, userCommunication) ===
    DraftSyncStatus.LOCAL_ONLY
    ? entry?.sessionId
    : undefined;
};

export const isServerDraftCommunication = (
  userCommunication: UserCommunication,
): boolean =>
  threadCarriesDraft(userCommunication) &&
  Boolean(resolveDraftId(userCommunication));

export const planCommunicationDeletion = (
  messageIds: string[],
  communications: UserCommunication[],
): CommunicationDeletionPlan => {
  const indexState = useComposeSnapshotIndexStore.getState();
  const { entriesBySessionId } = indexState;
  const byMessageId = new Map(
    communications.map((communication) => [
      communication.messageId,
      communication,
    ]),
  );
  const plan: CommunicationDeletionPlan = {
    serverDraftMessageIds: [],
    localSessionIds: [],
    otherIds: [],
  };

  for (const messageId of messageIds) {
    const communication = byMessageId.get(messageId);
    const localSessionId = isLocalDraftSelectionId(messageId)
      ? fromLocalDraftSelectionId(messageId)
      : communication &&
        resolveLocalOnlySessionId(
          communication,
          entriesBySessionId,
          indexState,
        );

    if (localSessionId) {
      plan.localSessionIds.push(localSessionId);
    } else if (communication && isServerDraftCommunication(communication)) {
      plan.serverDraftMessageIds.push(messageId);
    } else {
      plan.otherIds.push(messageId);
    }
  }
  return plan;
};
